import { tInteractiveOutbound } from '../../../../Chat.types';

// Drafts are scoped to (authId, workspaceId, chatId) so switching between
// chats doesn't leak work-in-progress between leads, and a shared
// browser / kiosk doesn't inherit another user's drafts after logout.
// Persisted to localStorage so the draft survives page reloads — the
// seller expects "I closed the composer and came back" to keep their
// fields populated.

const STORAGE_KEY_PREFIX = 'leadsales:interactive_draft';
const STORAGE_SCHEMA_VERSION = 1;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type tStoredDraft = {
  version: number;
  savedAt: number;
  draft: tInteractiveOutbound;
};

function storageKey(authId: string, workspaceId: string, chatId: string): string {
  return `${STORAGE_KEY_PREFIX}:${authId}:${workspaceId}:${chatId}`;
}

// localStorage can throw on private-mode Safari and on quota exceeded.
// Caching is a nice-to-have, not a correctness requirement, so every
// failure path returns a safe no-op.
function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Most likely QuotaExceededError. Draft lost but composer still works.
  }
}

function removeStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

const VALID_TYPES = new Set(['button', 'cta_url', 'list']);
const VALID_HEADER_TYPES = new Set(['text', 'image', 'video', 'document']);
const MEDIA_HEADER_KEYS = ['image', 'video', 'document'] as const;

// Structural sanity check against tampered payloads. Does NOT run the full
// Yup submit schema because the cached draft is intentionally incomplete
// (empty fields, partial arrays). Only rejects shapes that couldn't have
// come from the composer: wrong `type` discriminator, non-string scalars,
// non-object header/action. A malicious payload that passes this check
// still has its URLs re-validated at render time.
function isValidInteractiveDraftShape(
  x: unknown,
): x is tInteractiveOutbound {
  if (!x || typeof x !== 'object') return false;
  const d = x as Record<string, unknown>;
  if (typeof d.type !== 'string' || !VALID_TYPES.has(d.type)) return false;
  if (d.body !== undefined && typeof d.body !== 'string') return false;
  if (d.footer !== undefined && typeof d.footer !== 'string') return false;
  if (d.action !== undefined && (typeof d.action !== 'object' || d.action === null)) return false;
  if (d.header === undefined) return true;
  if (typeof d.header !== 'object' || d.header === null) return false;
  const h = d.header as Record<string, unknown>;
  if (typeof h.type !== 'string' || !VALID_HEADER_TYPES.has(h.type)) return false;
  if (h.text !== undefined && typeof h.text !== 'string') return false;
  for (const k of MEDIA_HEADER_KEYS) {
    const media = h[k];
    if (media === undefined) continue;
    if (typeof media !== 'object' || media === null) return false;
    const m = media as Record<string, unknown>;
    if (m.link !== undefined && typeof m.link !== 'string') return false;
    if (m.id !== undefined && typeof m.id !== 'string') return false;
  }
  return true;
}

export const InteractiveDraftCache = Object.freeze({
  load(
    authId: string,
    workspaceId: string,
    chatId: string,
  ): tInteractiveOutbound | undefined {
    if (!authId || !workspaceId || !chatId) return undefined;
    const key = storageKey(authId, workspaceId, chatId);
    const raw = readStorage(key);
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw) as tStoredDraft;
      if (parsed.version !== STORAGE_SCHEMA_VERSION) return undefined;
      if (
        typeof parsed.savedAt !== 'number' ||
        Date.now() - parsed.savedAt > DRAFT_TTL_MS
      ) {
        removeStorage(key);
        return undefined;
      }
      if (!isValidInteractiveDraftShape(parsed.draft)) {
        removeStorage(key);
        return undefined;
      }
      return parsed.draft;
    } catch {
      return undefined;
    }
  },

  save(
    authId: string,
    workspaceId: string,
    chatId: string,
    draft: tInteractiveOutbound,
  ): void {
    if (!authId || !workspaceId || !chatId) return;
    const payload: tStoredDraft = {
      version: STORAGE_SCHEMA_VERSION,
      savedAt: Date.now(),
      draft,
    };
    writeStorage(storageKey(authId, workspaceId, chatId), JSON.stringify(payload));
  },

  clear(authId: string, workspaceId: string, chatId: string): void {
    if (!authId || !workspaceId || !chatId) return;
    removeStorage(storageKey(authId, workspaceId, chatId));
  },

  // Purges every draft regardless of auth/workspace/chat. Call on logout so
  // a shared browser doesn't leak drafts between users.
  purgeAll(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(`${STORAGE_KEY_PREFIX}:`)) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) removeStorage(key);
    } catch {
      // Ignore.
    }
  },
});
